// src/components/PatientList.js
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, User, Phone, Mail, Edit, Trash2 } from 'lucide-react';
import './PatientList.css';
import { exportPatientsToExcel } from '../utils/exportPatients';

const PatientList = ({ patients, onViewPatient, onEditPatient, onDeletePatient }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredPatients, setFilteredPatients] = useState(patients);

  // Filter patients based on search term
  React.useEffect(() => {
    const filtered = patients.filter(patient =>
      patient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      patient.mrn.toLowerCase().includes(searchTerm.toLowerCase()) ||
      patient.phone.includes(searchTerm) ||
      patient.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredPatients(filtered);
  }, [searchTerm, patients]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  const cardHoverVariants = {
    hover: {
      y: -2,
      scale: 1.02,
      transition: {
        duration: 0.2
      }
    }
  };

  return (
    <motion.div
      className="patient-list"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Header */}
      <motion.div className="patient-list-header" variants={itemVariants}>
        <h1>Patient List</h1>
        <p>Manage and view all registered patients</p>
      </motion.div>
      
    <motion.div className="search-container" variants={itemVariants}>
      <div className="search-bar">
        <Search size={20} className="search-icon" />
        <input
          type="text"
          placeholder="Search by name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      <button
        className="export-btn"
        onClick={() => exportPatientsToExcel(filteredPatients)}
      >
        Export to Excel
      </button>
</motion.div>

      {/* Patient Count */}
      <motion.div className="patient-count" variants={itemVariants}>
        <span>{filteredPatients.length} patients found</span>
      </motion.div>

      {/* Patients Grid */}
      <motion.div className="patients-grid" variants={containerVariants}>
        <AnimatePresence>
          {filteredPatients.map((patient, index) => (
            <motion.div
              key={patient.id}
              className="patient-card"
              variants={itemVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              whileHover="hover"
              variant={cardHoverVariants}
              transition={{ delay: index * 0.1 }}
            >
              {/* Patient Header */}
              <div className="patient-header">
                <div className="patient-avatar">
                  <User size={20} />
                </div>
                <div className="patient-basic-info">
                  <h3 className="patient-name">{patient.name}</h3>
                  <span className="patient-mrn">{patient.mrn}</span>
                </div>
              </div>

              {/* Patient Contact Info */}
              <div className="patient-contact">
                <div className="contact-item">
                  <Phone size={16} className="contact-icon" />
                  <span>{patient.phone}</span>
                </div>
                <div className="contact-item">
                  <Mail size={16} className="contact-icon" />
                  <span>{patient.email}</span>
                </div>
              </div>

              {/* Vaccine Summary */}
              <div className="vaccine-summary">
                <div className="vaccine-stats">
                  <span className="vaccine-count">
                    {patient.vaccines.length} vaccine{patient.vaccines.length !== 1 ? 's' : ''}
                  </span>
                  <span className="completed-count">
                    {patient.vaccines.filter(v => v.status === 'completed').length} completed
                  </span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <motion.button
                  className="view-details-btn"
                  onClick={() => onViewPatient(patient)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  View Details
                </motion.button>
                <motion.button
                  className="icon-btn edit-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditPatient(patient);
                  }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  title="Edit Patient"
                >
                  <Edit size={18} />
                </motion.button>
                <motion.button
                  className="icon-btn delete-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeletePatient(patient.id);
                  }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  title="Delete Patient"
                >
                  <Trash2 size={18} />
                </motion.button>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>

      {/* Empty State */}
      {filteredPatients.length === 0 && (
        <motion.div
          className="empty-state"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <User size={48} className="empty-icon" />
          <h3>No patients found</h3>
          <p>Try adjusting your search criteria</p>
        </motion.div>
      )}
    </motion.div>
  );
};

export default PatientList;